import React, { Suspense, FC } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  NavLink
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './App.css';
import logoSrc from './logo-80.webp'; // Updated to use logo-80.webp
import i18n from './i18n'; // Import the configured i18n instance
import 'react-big-calendar/lib/css/react-big-calendar.css'; // Import calendar CSS
import 'leaflet/dist/leaflet.css'; // Import Leaflet CSS
import 'quill/dist/quill.snow.css'; // Import Quill CSS
import Footer from './Footer'; // Import the Footer component
import { AuthProvider, useAuth } from './AuthContext';

// Page Components
import HomePage from './HomePage';
import EventsPage from './EventsPage';
import OrganizationPage from './OrganizationPage';
import ProjectsPage from './ProjectsPage';
import ReportingPage from './ReportingPage';
import DonatePage from './DonatePage';
import CreateEventPage from './CreateEventPage';
import EditEventPage from './EditEventPage';
import LoginPage from './LoginPage';
import ProtectedRoute from './ProtectedRoute';
import CreateOrganizationPage from './CreateOrganizationPage';
import OrganizationUsersPage from './OrganizationUsersPage';
import EventDetailsPage from './EventDetailsPage';

interface NavLinkRenderProps {
  isActive: boolean;
}

const AppContent: FC = () => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();

  const changeLanguage = (lng: string): void => {
    i18n.changeLanguage(lng);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const navLinkClassName = ({ isActive }: NavLinkRenderProps): string =>
    isActive ? "nav-link active" : "nav-link";

  const donateButtonClassName = ({ isActive }: NavLinkRenderProps): string =>
    isActive ? "nav-link button-donate active" : "nav-link button-donate";

  return (
    <Router>
      <div className="App">
        <header className="app-header">
          <div className="header-container">
            <div className="logo-container">
              <Link to="/" className="logo-text-link">
                <img src={logoSrc} alt={t('selfreliance_logo_alt')} className="logo-img" /> 
                <span className="organization-name">{t('selfreliance_name')}</span>
              </Link>
            </div>
            <nav className="main-nav">
              <ul className="navbar-links">
                <li><NavLink to="/events" className={navLinkClassName}>{t('events')}</NavLink></li>
                <li><NavLink to="/organization" className={navLinkClassName}>{t('organization')}</NavLink></li>
                {/* <li><NavLink to="/projects" className={navLinkClassName}>{t('projects')}</NavLink></li> */}
                {/* <li><NavLink to="/reporting" className={navLinkClassName}>{t('reporting')}</NavLink></li> */}
              </ul>
            </nav>
            <div className="header-actions">
              <div className="language-switcher">
                <button onClick={() => changeLanguage('en')} className={`lang-button ${i18n.language === 'en' ? 'active' : ''}`}>EN</button>
                <button onClick={() => changeLanguage('uk')} className={`lang-button ${i18n.language === 'uk' ? 'active' : ''}`}>UA</button>
              </div>
              {user ? (
                <button onClick={handleLogout} className="button-secondary">{t('logout')}</button>
              ) : (
                <Link to="/login" className="button-secondary">{t('login')}</Link>
              )}
              {/* <NavLink to="/donate" className={donateButtonClassName}>
                {t('donate')} <span role="img" aria-label={t('ukrainian_flag_heart_alt')}>🇺🇦❤️</span>
              </NavLink> */}
            </div>
          </div>
        </header>

        <main className="main-content">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/events/:id" element={<EventDetailsPage />} />
            <Route path="/events/new" element={
              <ProtectedRoute>
                <CreateEventPage />
              </ProtectedRoute>
            } />
            <Route path="/events/:id/edit" element={
              <ProtectedRoute>
                <EditEventPage />
              </ProtectedRoute>
            } />
            <Route path="/organization" element={<OrganizationPage />} />
            <Route path="/organization/new" element={
              <ProtectedRoute>
                <CreateOrganizationPage />
              </ProtectedRoute>
            } />
            <Route path="/organization/:orgId/users" element={
              <ProtectedRoute>
                <OrganizationUsersPage />
              </ProtectedRoute>
            } />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/reporting" element={<ReportingPage />} />
            <Route path="/donate" element={<DonatePage />} />
            <Route path="/login" element={<LoginPage />} />
          </Routes>
        </main>

        <Footer />
      </div>
    </Router>
  );
}

const App: FC = () => (
  <Suspense fallback="Loading...">
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  </Suspense>
);

export default App;
